import { useQuery } from '@tanstack/react-query';
import { BaseService } from '@/services/base/base.service';
import { getPermissionName } from '@/constants/permissions';
import { resolveRuntimeApiGatewayUrl } from '@/config/runtime-api';

/**
 * ABP Application Configuration Response
 * Contains granted policies (permissions) for the current user
 */
interface ApplicationConfiguration {
  auth?: {
    grantedPolicies?: Record<string, boolean>;
  };
}

/**
 * Service to fetch application configuration from ABP
 */
class ApplicationConfigurationService extends BaseService {
  constructor() {
    super('');
  }

  async getConfiguration(): Promise<ApplicationConfiguration> {
    const baseUrl = resolveRuntimeApiGatewayUrl();
    const { data } = await this.api.get<ApplicationConfiguration>(
      `${baseUrl}/api/abp/application-configuration`
    );
    return data;
  }
}

const configService = new ApplicationConfigurationService();

/**
 * Hook to fetch current user's granted permissions
 * 
 * Fetches ABP's application-configuration endpoint which contains
 * all permissions granted to the current user in auth.grantedPolicies
 * 
 * Results are cached for 5 minutes
 * 
 * @returns React Query result with granted policies
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data: policies, isLoading } = useGrantedPolicies();
 *   
 *   if (isLoading) return <Spinner />;
 *   
 *   const canEdit = policies?.['MyApp.Edit'] === true;
 *   return <Button disabled={!canEdit}>Edit</Button>;
 * }
 * ```
 */
export function useGrantedPolicies() {
  return useQuery({
    queryKey: ['application-configuration', 'granted-policies'],
    queryFn: async () => {
      const config = await configService.getConfiguration();
      return config.auth?.grantedPolicies || {};
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    retry: 1,
  });
}

/**
 * Hook to check if current user has a specific permission
 * 
 * Checks against ABP's grantedPolicies from application-configuration
 * Supports legacy permission names via mapping
 * 
 * @param permission - Permission name to check (e.g., 'Gamification.Wallet.AdminAdjust')
 * @param requireAll - If true, all permissions must be granted (for array input)
 * @returns Object with hasPermission boolean and loading state
 * 
 * @example
 * ```tsx
 * function EditButton() {
 *   const { hasPermission, isLoading } = useHasPermission('MyApp.Edit');
 *   
 *   if (isLoading) return <Spinner />;
 *   if (!hasPermission) return null;
 *   
 *   return <Button>Edit</Button>;
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Check multiple permissions (requireAll: true means AND)
 * function AdminPanel() {
 *   const { hasPermission } = useHasPermission(
 *     ['MyApp.Edit', 'MyApp.Delete'],
 *     true
 *   );
 *   
 *   if (!hasPermission) return <AccessDenied />;
 *   return <AdminControls />;
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Check multiple permissions (requireAll: false means OR)
 * function ViewerPanel() {
 *   const { hasPermission } = useHasPermission(
 *     ['MyApp.View', 'MyApp.ViewDetails'],
 *     false
 *   );
 *   
 *   return hasPermission ? <Content /> : <AccessDenied />;
 * }
 * ```
 */
export function useHasPermission(
  permission: string | string[],
  requireAll: boolean = false
): {
  hasPermission: boolean;
  isLoading: boolean;
  isError: boolean;
} {
  const { data: grantedPolicies, isLoading, isError } = useGrantedPolicies();

  // If loading or error, return conservative values
  if (isLoading || isError || !grantedPolicies) {
    return {
      hasPermission: false,
      isLoading,
      isError: isError || false,
    };
  }

  // Handle single permission
  if (typeof permission === 'string') {
    const normalizedPermission = getPermissionName(permission);
    const hasPermission = grantedPolicies[normalizedPermission] === true;
    
    return {
      hasPermission,
      isLoading: false,
      isError: false,
    };
  }

  // Handle multiple permissions
  if (Array.isArray(permission)) {
    const normalizedPermissions = permission.map(getPermissionName);
    
    let hasPermission: boolean;
    if (requireAll) {
      // All permissions must be granted (AND logic)
      hasPermission = normalizedPermissions.every(
        (perm) => grantedPolicies[perm] === true
      );
    } else {
      // At least one permission must be granted (OR logic)
      hasPermission = normalizedPermissions.some(
        (perm) => grantedPolicies[perm] === true
      );
    }
    
    return {
      hasPermission,
      isLoading: false,
      isError: false,
    };
  }

  // Fallback
  return {
    hasPermission: false,
    isLoading: false,
    isError: false,
  };
}

/**
 * Hook to check multiple permission scenarios
 * 
 * Useful when you need to check different permission combinations
 * 
 * @param permissions - Array of permission scenarios to check
 * @returns Object with permission check results for each scenario
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const permissions = usePermissions({
 *     canView: 'MyApp.View',
 *     canEdit: 'MyApp.Edit',
 *     canDelete: 'MyApp.Delete',
 *     isAdmin: ['MyApp.Admin', 'MyApp.SuperAdmin'], // OR logic
 *   });
 *   
 *   if (permissions.isLoading) return <Spinner />;
 *   
 *   return (
 *     <div>
 *       {permissions.canView && <ViewButton />}
 *       {permissions.canEdit && <EditButton />}
 *       {permissions.canDelete && <DeleteButton />}
 *       {permissions.isAdmin && <AdminPanel />}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePermissions<T extends Record<string, string | string[]>>(
  permissions: T
): {
  [K in keyof T]: boolean;
} & {
  isLoading: boolean;
  isError: boolean;
} {
  const { data: grantedPolicies, isLoading, isError } = useGrantedPolicies();

  // If loading or error, return conservative values
  if (isLoading || isError || !grantedPolicies) {
    const permissionChecks = {} as { [K in keyof T]: boolean };

    (Object.keys(permissions) as Array<keyof T>).forEach((key) => {
      permissionChecks[key] = false;
    });

    return {
      ...permissionChecks,
      isLoading,
      isError: isError || false,
    };
  }

  const permissionChecks = {} as { [K in keyof T]: boolean };

  // Check each permission scenario
  (Object.entries(permissions) as Array<[keyof T, string | string[]]>).forEach(([key, permission]) => {
    if (typeof permission === 'string') {
      const normalizedPermission = getPermissionName(permission);
      permissionChecks[key] = grantedPolicies[normalizedPermission] === true;
    } else if (Array.isArray(permission)) {
      // For arrays, use OR logic (at least one must be granted)
      const normalizedPermissions = permission.map(getPermissionName);
      permissionChecks[key] = normalizedPermissions.some(
        (perm) => grantedPolicies[perm] === true
      );
    }
  });

  return {
    ...permissionChecks,
    isLoading: false,
    isError: false,
  };
}
